import type { components } from "@sdcp/types";
import { documentUrl } from "@sdcp/api-client";

type Document = NonNullable<components["schemas"]["TurnOut"]["document"]>;

export interface DocumentCardProps {
  document: Document;
  /** Withdraw this document. Absent for anything the borrower cannot change. */
  onReplace?: ((documentId: string) => void) | undefined;
  busy?: boolean | undefined;
}

/**
 * A collected document, shown where it arrived in the conversation.
 *
 * The borrower gave up access to their bank. A progress number moving from 1/5
 * to 2/5 is not an account of what they got back — this is.
 */
export function DocumentCard({ document, onReplace, busy = false }: DocumentCardProps) {
  // A withdrawn document stays in the conversation — it is a record of what
  // happened — but it stops being a live link to something the lender no longer
  // sees, and it says so rather than looking merely greyed out.
  if (document.replaced) {
    return (
      <div className="mt-3 flex items-center gap-3 rounded-lg border border-border-soft bg-transparent px-3 py-3 opacity-60">
        <span
          aria-hidden
          className="grid size-10 shrink-0 place-items-center rounded-md bg-bg-app font-mono text-[10px] font-bold text-ink-400"
        >
          PDF
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold line-through">{document.label}</span>
          <span className="block truncate text-xs text-ink-500">
            Replaced — your loan officer does not see this
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-1.5">
    <a
      href={documentUrl(document.id)}
      target="_blank"
      rel="noreferrer"
      className="mt-3 flex items-center gap-3 rounded-lg border border-border-cool bg-surface px-3 py-3 transition-colors hover:border-verity-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verity-ring"
    >
      <span
        aria-hidden
        className="grid size-10 shrink-0 place-items-center rounded-md bg-verity-050 font-mono text-[10px] font-bold text-verity-700"
      >
        PDF
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold">{document.label}</span>
        <span className="block truncate text-xs text-ink-500">
          {document.provenance}
          {document.page_count ? ` · ${document.page_count} pages` : ""}
        </span>
      </span>

      <span aria-hidden className="shrink-0 text-ink-400">
        ↗
      </span>
    </a>

      {/* The way out of a mistake. Sitting under the document rather than in a
          menu, because someone who has just sent the wrong file is looking
          straight at it. */}
      {onReplace && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onReplace(document.id)}
          className="self-start px-1 text-xs text-ink-500 underline underline-offset-2 transition-colors hover:text-verity-700 disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verity-ring"
        >
          Wrong file? Replace it
        </button>
      )}
    </div>
  );
}
